import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import uuidv1 from 'uuid/v1';
import { ongoingBookingRefreshIntervalInMsSelector } from 'selectors/app-selector';
import {
  ongoingBookingFilterSelector,
  ongoingDeliveriesV2Selector,
  ongoingIntervalIdSelector,
} from 'selectors/new-history-selectors';
import {
  GE_WEB_ORDER_STATE,
  NEW_COMPLETED_ORDER_STATE_FILTER,
  NEW_ONGOING_ORDER_STATE_FILTER,
  ORDER_TYPES,
  ORDER_TYPES_SOURCE_ENUMS,
} from 'constants/order-constants';
import { APP_BASE_URL_PATH } from 'constants/app-constants';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import NewHistoryDuck from 'ducks/new-history-duck';
import ScribeDuck from 'ducks/scribe-duck';

export const useIntervalOngoingFetch = (page = 'root') => {
  const dispatch = useDispatch();
  const config = useSelector(ongoingBookingRefreshIntervalInMsSelector);
  const { orderState } = useSelector(ongoingBookingFilterSelector);
  const intervalId = useSelector(ongoingIntervalIdSelector);

  // init fetch
  useEffect(() => {
    dispatch(
      NewHistoryDuck.creators.updateOngoingBookingFilter({
        pageSize: '5',
        orderType: ORDER_TYPES.ALL,
        orderState: orderState || GE_WEB_ORDER_STATE.ActiveDelivery,
      }),
    );

    dispatch(NewHistoryDuck.creators.fetchOngoingBookings());
  }, []);

  // interval fetch
  useEffect(
    () => {
      // sanity only 1 interval by page that run in nested component
      if (intervalId[page]) clearInterval(intervalId[page]);

      if (config !== null && config > 0) {
        const id = setInterval(
          () => dispatch(NewHistoryDuck.creators.fetchOngoingBookings()),
          config,
        );
        dispatch(NewHistoryDuck.creators.setOngoingIntervalId(page, id));
        return () => clearInterval(id);
      }
    },
    [config],
  );
};

export const useFilteredOrder = (
  deliveries,
  selectedOrderState,
  selectedOrderType,
) => {
  const filterByOrderState = useCallback(
    delivery => {
      // safety check - if delivery is undefined or doesn't have status property
      if (_isEmpty(delivery)) {
        return false;
      }

      // don't filter
      if (
        selectedOrderState === GE_WEB_ORDER_STATE.AllStatus ||
        selectedOrderState === GE_WEB_ORDER_STATE.ActiveDelivery
      ) {
        return true;
      }

      // no filter config
      const filterConfig =
        NEW_ONGOING_ORDER_STATE_FILTER[selectedOrderState] ||
        NEW_COMPLETED_ORDER_STATE_FILTER[selectedOrderState];
      if (!filterConfig) {
        return false;
      }

      const { matchStates } = filterConfig;
      if (matchStates === undefined) {
        // match all
        return true;
      }
      return matchStates.includes(delivery.status);
    },
    [selectedOrderState],
  );

  const filterByOrderType = useCallback(
    delivery => {
      // safety check - if delivery is undefined
      if (_isEmpty(delivery)) {
        return false;
      }

      if (selectedOrderType === ORDER_TYPES.ALL) {
        return true;
      }
      const source = delivery.source || 0;
      return source === ORDER_TYPES_SOURCE_ENUMS[selectedOrderType];
    },
    [selectedOrderType],
  );

  return useMemo(
    () => {
      // Make sure deliveries is actually an array before filtering
      if (!Array.isArray(deliveries) || _isEmpty(deliveries)) {
        return [];
      }

      return deliveries.filter(filterByOrderState).filter(filterByOrderType);
    },
    [deliveries, filterByOrderState, filterByOrderType],
  );
};

export const useFilteredOngoingOrder = () => {
  const { deliveries } = useSelector(ongoingDeliveriesV2Selector);
  const { orderState, orderType } = useSelector(ongoingBookingFilterSelector);

  return useFilteredOrder(deliveries, orderState, orderType);
};

export const usePrintLabels = ({ orders, isBulk = false }) => {
  const dispatch = useDispatch();

  return useCallback(
    () => {
      if (_isEmpty(orders)) return;

      if (isBulk) {
        dispatch(
          ScribeDuck.creators.scribeExpressBookingsWebBulkPrintLabel(
            orders.length,
          ),
        );
      } else {
        dispatch(ScribeDuck.creators.scribeExpressBookingsWebPrintLabel());
      }

      const windowName = uuidv1();
      dispatch(NewHistoryDuck.creators.updateWaybill(windowName, orders));

      const path =
        window.location.origin + APP_BASE_URL_PATH + ROUTES_V2_PATHS.waybills;
      window.open(path, windowName);
    },
    [dispatch, orders, isBulk],
  );
};

export default {};
